import React, { useState } from "react";
import { FiArrowLeft, FiArrowRight } from "react-icons/fi";
import Header from "../../component/header/header";
import { templateData } from "../Templates/demoData";
import Button from "../../component/button/button";
import { useNavigate, useParams } from "react-router-dom";
import ViewModal from "../Modal/ViewModal";
import SubjectSelector from "../../component/button/MultiSubjectSelector";

interface Subject {
  _id: string;
  title: string;
}

interface Group {
  id: number;
  name: string;
  selectedSubjects: Subject[];
}

export default function SelectTopics() {
  const navigate = useNavigate();
  const { subjectId } = useParams();
  const topicData = templateData.find((item) => item._id === subjectId);

  const [groups, setGroups] = useState<Group[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSubjects, setSelectedSubjects] = useState<Subject[]>([]);
  const [activeGroupId, setActiveGroupId] = useState<number | null>(null);

  const handleAddGroup = () => {
    const newGroup: Group = {
      id: Date.now(),
      name: "",
      selectedSubjects: [],
    };
    setGroups((prev) => [...prev, newGroup]);
  };

  const handleGroupNameChange = (id: number, name: string) => {
    setGroups((prev) =>
      prev.map((group) => (group.id === id ? { ...group, name } : group))
    );
  };

  const openSubjectModal = (groupId: number) => {
    setActiveGroupId(groupId);
    const currentGroup = groups.find((g) => g.id === groupId);
    setSelectedSubjects(currentGroup?.selectedSubjects || []);
    setIsOpen(true);
  };

  const handleAddSubjectsToGroup = () => {
    if (activeGroupId === null) return;

    setGroups((prev) =>
      prev.map((group) =>
        group.id === activeGroupId
          ? {
              ...group,
              selectedSubjects: selectedSubjects,
            }
          : group
      )
    );

    setIsOpen(false);
    setSelectedSubjects([]);
    setActiveGroupId(null);
  };

  const handleRemoveSubject = (groupId: number, subjectId: string) => {
    setGroups((prev) =>
      prev.map((group) =>
        group.id === groupId
          ? {
              ...group,
              selectedSubjects: group.selectedSubjects.filter(
                (s) => s._id !== subjectId
              ),
            }
          : group
      )
    );
  };

  const handleNext = () => {
    for (let group of groups) {
      if (!group.name.trim()) {
        alert("Please enter a subject group name.");
        return;
      }
      if (group.selectedSubjects.length === 0) {
        alert("Please select at least one subject in each group.");
        return;
      }
    }

    navigate("/exams/create-exam/set-question", {
      state: {
        groups,
      },
    });
  };

  return (
    <div className="content exams">
      <Header
        pageTitle="Select Topics"
        buttonText="Back"
        onClick={() => navigate(-1)}
        para={
          <span>
            <p>
              Pick the topic you want to include in the exam. You can create
              multiple groups with different subjects.
            </p>
          </span>
        }
        buttonIcon={<FiArrowLeft />}
        extraButton={
          <Button
            text="Next"
            onClick={handleNext}
            icon={<FiArrowRight />}
            type="secondary"
          />
        }
      />

      <div className="flex w-full flex-col gap-4 mt-3">
        <div className="flex w-[70%] flex-col gap-4">
          <div className="container m-auto flex flex-col">
            <div className="w-full">
              <button
                className="w-full border border-dashed border-gray-400 flex flex-col items-center mr-[5px] p-2"
                onClick={handleAddGroup}
              >
                <span className="text-2xl">+</span>
                <span className="text-sm">Add a Group</span>
              </button>
            </div>

            {groups.map((group) => (
              <div
                key={group.id}
                className="mt-4 border border-gray-300 p-4 rounded bg-white"
              >
                <div className="flex gap-4 items-center mb-3">
                  <input
                    value={group.name}
                    onChange={(e) =>
                      handleGroupNameChange(group.id, e.target.value)
                    }
                    placeholder="Enter group name"
                    className="w-[80%] border border-gray-300 p-2 rounded outline-none focus:ring focus:ring-blue-200"
                  />
                  <button
                    className="flex items-center gap-2 border border-gray-600 p-2 rounded"
                    onClick={() => openSubjectModal(group.id)}
                  >
                    <span className="text-2xl leading-none">+</span>
                    <span>Add Subjects</span>
                  </button>
                </div>

                {group.selectedSubjects.length > 0 && (
                  <div className="flex flex-wrap gap-2">
                    {group.selectedSubjects.map((subject) => (
                      <div
                        key={subject._id}
                        className="flex items-center bg-gray-700 text-white rounded overflow-hidden mr-3"
                      >
                        <span className="px-4 text-sm">{subject.title}</span>
                        <button
                          onClick={() =>
                            handleRemoveSubject(group.id, subject._id)
                          }
                          className="bg-white text-gray-800 px-3 text-lg hover:text-red-500 border border-gray-200"
                          title="Remove"
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <ViewModal
          title="Select Subjects to Add"
          isOpen={isOpen}
          onClose={() => setIsOpen(false)}
          width="1000px"
          extraBtn={true}
          extraBtnName="Proceed"
          extraBtnType="secondary"
          handleExtraButtonClick={handleAddSubjectsToGroup}
          body={
            <div>
              <h3 className="text-2xl font-semibold text-left mb-1">
                Select Subjects to Add
              </h3>
              <div className="mb-6">
                {Array.isArray(topicData?.subtopic) && (
                  <SubjectSelector
                    subjects={topicData.subtopic}
                    selectedSubjects={selectedSubjects}
                    onSelect={setSelectedSubjects}
                  />
                )}
              </div>
            </div>
          }
        />
      </div>
    </div>
  );
}
